/* eslint-disable no-console */
import { atom, useSetAtom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';
import { waRefreshAtom } from '../state';

export const forceRerenderUIAtom = atom<string>('');
export const orderbyAtom = atomWithReset<string>('vuln');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('desc');
export const orderingAtom = atom(async (get) => {
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  return `${order === 'desc' ? '-' : ''}${orderBy}`;
});

export const DEFAULT_ACTIVE_WA_LISTING_COLUMNS = [
  'name',
  'url',
  'vuln',
  'webapp_created_date',
  'last_scan_date',
  'results_status',
  'auth_status',
  'actions',
];
export const activeWAColumnKeysAtom = atomWithStorage(
  'wa_list_active_column_keys',
  DEFAULT_ACTIVE_WA_LISTING_COLUMNS,
);

export const DEFAULT_DEACTIVED_WA_LISTING_COLUMNS = [
  'name',
  'url',
  'vuln',
  'last_scan_date',
  'results_status',
  'auth_status',
  'deactivated_by',
  'last_deactivated_at',
  'actions',
];
export const deactivatedWAColumnKeysAtom = atomWithStorage(
  'wa_list_deactivated_column_keys',
  DEFAULT_DEACTIVED_WA_LISTING_COLUMNS,
);

export const DEFAULT_AGED_OUT_WA_LISTING_COLUMNS = [
  'name',
  'url',
  'deleted_webapp__open_vuln_counts',
  'deleted_webapp__closed_vuln_counts',
  'deactivated_by',
  'last_deactivated_at',
  'deleted_webapp__deleted_by',
  'deleted_webapp__deleted_at',
  'actions',
];
export const agedOutWAColumnKeysAtom = atomWithStorage(
  'wa_list_aged_out_column_keys',
  DEFAULT_AGED_OUT_WA_LISTING_COLUMNS,
);

export const paginationConfigAtom = atomWithStorage(
  'wa_dashboard_pagination_config',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);

export const checkedListAtom = atomWithReset<number[]>([]);
export const isCheckAllAtom = atomWithReset(false);

// component height
export const componentHeightAtom = atom(0);

// Add a new atom to store vulnerability counts for checked hosts
export const checkedHostsVulnMapAtom = atom<Record<number, number>>({});

export const vulnCountForCheckedHostsAtom = atom(async (get) => {
  const checkedHostIds = get(checkedListAtom);
  const vulnMap = get(checkedHostsVulnMapAtom);
  let total = 0;

  if (checkedHostIds?.length > 0) {
    const allHosts = get(waListingDataAtom);

    // Update vulnMap with any new hosts from current page
    const currentPageHosts = allHosts?.items?.filter((host) =>
      checkedHostIds.includes(host.id),
    );

    // Calculate total using both stored and current page values
    checkedHostIds.forEach((hostId) => {
      // Use stored value if available, otherwise 0
      total += vulnMap[hostId] || 0;
    });

    // Update totals with any new values from current page
    currentPageHosts?.forEach((host) => {
      if (host.vuln !== vulnMap[host.id]) {
        total = total - (vulnMap[host.id] || 0) + (host.vuln || 0);
      }
    });
  }
  return total;
});

interface Insights {
  status_breakdown: {
    active: number;
    inactive: number;
    deleted: number;
  };
  severity_breakdown: {
    urgent: number;
    critical: number;
    serious: number;
    medium: number;
    minimal: number;
  };
  scan_status_breakdown: {
    running: number;
    error: number;
    auth_failed: number;
    no_web_service: number;
  };
  total_vulnerabilities: number;
}

// export const waListingDataAtom = atomWithDefault(async (get) => {

export const waListingDataAtom = atom({
  items: [] as any[],
  count: 0,
  loading: false,
  insights: {} as Insights,
});

// New filter system state atoms
const DateFilterKeys = [
  'created_at',
  'updated_at',
  'latest_scan__launch_datetime',
  'last_deactivated_at',
  'last_reactivated_at',
];
export const filterValuesAtom = atomWithReset<Record<string, any>>({
  filter: ['active'],
});
export const isQuerystringFiltersInitializedAtom =
  atomWithReset<boolean>(false);
export const waDataQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const pageSize = paginationConfig.page_size;
  const currentPage = paginationConfig.page;
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);

  // Add id to orderBy if not present, then apply sort order
  const orderByWithId = orderBy
    .split(',')
    .some((field) => field.trim() === 'id' || field.trim() === '-id')
    ? orderBy
    : `${orderBy},id`;
  const ordering = orderByWithId
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');
  const filterValues = get(filterValuesAtom);
  const _ = get(waRefreshAtom);

  if (companyId && companyId !== -1) {
    const params = {
      company_id: companyId.toString(),
      offset: pageSize * (currentPage - 1),
      limit: pageSize,
      ordering: ordering,
    };

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value.length > 0) {
        if (DateFilterKeys.includes(key)) {
          const fromDate = value[0]?.from;
          const toDate = value[0]?.to;
          if (fromDate instanceof Date) {
            params[`${key}_after`] = fromDate.toISOString();
          } else {
            console.error({
              msg: 'fromDate is not a valid Date object:',
              fromDate,
            });
          }
          if (toDate) params[`${key}_before`] = toDate?.toISOString();
        } else if (key === 'filter') {
          if (value != 'all') params[key] = value.join(',');
        } else {
          params[key] = value.join(',');
        }
      }
    });

    return params;
  }
});

// Reset function for filter changes
export const useOnFilterChangeResetAtoms = () => {
  const setCheckedList = useSetAtom(checkedListAtom);
  const setPaginationConfig = useSetAtom(paginationConfigAtom);

  return () => {
    setCheckedList([]);
    setPaginationConfig((prev) => ({ ...prev, page: 1 }));
  };
};
